import React, { useCallback, useMemo, useState } from "react";
import styles from "./HomePageReviews.module.scss";
import Container from "~/components/Container";
import Star from "~/components/Star";
import Text from "~/components/Text";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import classNames from "classnames";
import useClient from "~/hooks/useClient";
import { QUERY_GET_ALL_REVIEWS_ONLY_IMG } from "~/data/home";
import { useQuery } from "@apollo/client";
import Image from "next/image";
import { formatDate } from "~/services/sneaker-release/sneaker-release-transform";
import { formatNumber } from "~/utils/number";
import { isUsingLayoutV2 } from "~/utils/theme";
import ModalCarousel from "~/components/ImagesReviewCarousel/ModalCarousel";

interface HomePageReviewsProps {
  siteReviews?: any;
}

const HomePageReviews = ({ siteReviews }: HomePageReviewsProps) => {
  const isClient = useClient();

  const [swiperRef, setSwiperRef] = useState<SwiperClass>({} as SwiperClass);
  const [openModal, setOpenModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const allReview = siteReviews?.allReview;
  const _slidesPerView =
    (swiperRef?.params?.slidesPerView as number) || (4 as number);

  const {
    data: dataReviews,
    loading: loadingReviews,
    fetchMore,
  } = useQuery(QUERY_GET_ALL_REVIEWS_ONLY_IMG, {
    variables: {
      images: true,
      pageSize: 4,
      currentPage: 1,
    },
    context: { fetchOptions: { method: "GET" } },
  });

  const _list = useMemo(() => {
    return (isClient ? dataReviews : siteReviews)?.allReview?.items ?? [];
  }, [dataReviews, isClient]);

  const page_info = useMemo(() => {
    return {
      total_page: dataReviews?.allReview?.page_info?.total_pages,
      current_page: dataReviews?.allReview?.page_info?.current_page,
    };
  }, [dataReviews]);

  const hasNext = page_info?.total_page > page_info?.current_page;

  const handleFetchMore = useCallback(() => {
    const current = page_info.current_page + 1;
    if (current <= page_info?.total_page) {
      fetchMore({
        variables: {
          images: true,
          pagesize: _slidesPerView ?? 4,
          currentPage: current,
        },
        updateQuery(previousQueryResult, options) {
          const { fetchMoreResult } = options;
          if (!fetchMoreResult) return previousQueryResult;
          const result = Object.assign({}, previousQueryResult, {
            allReview: {
              ...fetchMoreResult.allReview,
              items: [
                ...previousQueryResult.allReview.items,
                ...fetchMoreResult.allReview.items,
              ],
            },
          });
          return result;
        },
      });
    }
  }, [page_info]);

  const handleRealIndexChange = useCallback(
    (swiper: SwiperClass) => {
      if (!hasNext) return;
      if (!!loadingReviews) return;
      // if (products.length > MAX_RECORD) return;
      const currentIndex = swiper?.realIndex ?? 0;
      const silderPerView =
        typeof swiper?.params?.slidesPerView === "number"
          ? swiper?.params?.slidesPerView
          : 3;

      const remainingSlides = _list.length - currentIndex;

      if (remainingSlides <= silderPerView) {
        return handleFetchMore?.();
      }
    },
    [_list]
  );

  const allImages = useMemo(() => {
    return _list.flatMap((review: any) => review.images || []);
  }, [_list]);

  const handleOpenReview = useCallback(
    (reviewIndex: number) => {
      const imageIndex = _list
        .slice(0, reviewIndex)
        .reduce(
          (acc: number, review: any) => acc + (review.images?.length || 0),
          0
        );
      setSelectedImageIndex(imageIndex);
      setOpenModal(true);
    },
    [_list]
  );

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  return (
    <Container>
      <div className={styles.reviewsHeader}>
        <h3>
          {" "}
          {Number.isNaN(allReview?.summary)
            ? 0
            : formatNumber((allReview?.summary / 100) * 5, 1)}{" "}
        </h3>
        <div className={styles.ratingWrapper}>
          <div className={styles.stars}>
            <Star
              value={allReview?.summary ? (allReview?.summary / 100) * 5 : 1}
              size={16}
            />
          </div>
          <div
            className={styles.ratingsCount}
          >{`${allReview?.total_count ?? 0} customer ratings`}</div>
        </div>
      </div>
      {!!_list.length && (
        <Swiper
          onSwiper={setSwiperRef}
          // onAfterInit={handleAfterInit}
          slidesPerView={2}
          // loop={true}
          navigation={{
            enabled: true,
          }}
          slidesPerGroup={2}
          breakpoints={{
            768: {
              slidesPerView: 3,
              slidesPerGroup: 3,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 4,
              slidesPerGroup: 4,
              spaceBetween: 20,
            },
          }}
          onRealIndexChange={handleRealIndexChange}
          modules={[Navigation]}
        >
          {_list?.map((data: any, index: number) => {
            return (
              <SwiperSlide
                key={index}
                data-sku={data.sku}
                className="mr-[20px]"
              >
                <div
                  className={styles.reviewCard}
                  style={{ borderRadius: isUsingLayoutV2 ? 0 : "8px" }}
                  onClick={() => handleOpenReview(index)}
                >
                  <Image
                    src={data?.images[0] || data?.thumbnail?.url || ""}
                    alt="Customer 1"
                    width={260}
                    height={180}
                    priority={false}
                    className="w-full object-contain cursor-pointer"
                  />
                  <div className={styles.reviewContent}>
                    <div className={styles.reviewMeta}>
                      <div className="flex items-center">
                        <p className="!mb-0 truncate">{data?.nickname ?? ""}</p>
                        <img
                          src="/svgs/checked-circle.svg"
                          alt="Checked"
                          width={15}
                          height={15}
                          className="ml-2"
                        />
                      </div>{" "}
                      <Star
                        value={
                          data?.average_rating
                            ? (data?.average_rating / 100) * 5
                            : 1
                        }
                        size={16}
                      />
                    </div>
                    <div className="mb-3">
                      <small>
                        {data?.created_at ? formatDate(data.created_at) : ""}
                      </small>
                    </div>
                    <p className={styles.reviewText}>
                      {data?.text || "No review text available."}
                    </p>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
          {hasNext &&
            Array.from({ length: _slidesPerView }, (item, index: number) => {
              return (
                <SwiperSlide className="px-2" key={index}>
                  <div className={styles.reviewCard}>
                    <div
                      className={classNames(
                        styles.skeleton,
                        styles.skeletonImg
                      )}
                    ></div>
                    <div className={styles.reviewContent}>
                      <div
                        className={classNames(
                          styles.skeleton,
                          styles.skeletonMeta
                        )}
                      ></div>
                      <div
                        className={classNames(
                          styles.skeleton,
                          styles.skeletonStars
                        )}
                      ></div>
                      <div
                        className={classNames(
                          styles.skeleton,
                          styles.skeletonText
                        )}
                      ></div>
                      <div
                        className={classNames(
                          styles.skeleton,
                          styles.skeletonText
                        )}
                      ></div>
                      <div
                        className={classNames(
                          styles.skeleton,
                          styles.skeletonText
                        )}
                        style={{ width: "80%" }}
                      ></div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
        </Swiper>
      )}
      {!_list.length && (
        <div className="flex items-center justify-center h-[200px] w-full ">
          <Text type="title-3">Not Found Reviews</Text>
        </div>
      )}
      <ModalCarousel
        open={openModal}
        onClose={handleCloseModal}
        initialSlide={selectedImageIndex}
        images={allImages}
        items={_list}
      />
    </Container>
  );
};

export default HomePageReviews;
